import React, { useEffect, useState } from 'react';
import { Button, View } from '../../../../core/components/Index';
import { useNavigate, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import { generateStepArray } from './Utils';
import { BrowserRouter as Router, Outlet } from 'react-router-dom';
import { SpcFormProvider, useForm } from './SpcFormContext';
import SidebarFormNavigation, { SpcStep, SpcStepStatus } from './SpcSidebarFormNavigation';
import { useLocation } from 'react-router-dom';
import SraModule from '../SpcModule';
import { SpcSteps } from './SpcModels';
import { Appl } from '../../../../core/Appl';
import SpcViewModel from './SpcViewModel';
import SpcServiceType from './1_0_SpcServiceType';
import AboutEstimate from './2_0_AboutEstimate';
import EnterDetails from './3_0_EnterDetails';
import Estimate from './5_0_Estimate';
import Summary from './4_0_Summary';
import SpcSavedEstimates from './0_1_SpcSavedEstimates';
import useAsyncEffect from 'use-async-effect';

function useActiveStep(steps: SpcStep[], stepArray: any, location: any) {
  const pathSegments = location.pathname.split('/').filter(Boolean);

  const findMatchingStep = (steps: SpcStep[]): { activePage: SpcStep | null; nextStep: any; activeSubPage: SpcStep['subSteps'][0] | null } => {
    for (const step of steps) {
      if (pathSegments.includes(step.slug)) {
        let nextSlug = stepArray.findIndex((s: any) => s.slug === step.slug);

        const subStep = step.subSteps?.find((sub) => pathSegments.includes(sub.slug)) || null;
        if (subStep) {
          nextSlug = stepArray.findIndex((s: any) => s.slug === step.slug);
        }
        //find index of step or if set substep in stepArray by slug and return it then +1 to get the next step
        return { activePage: step, activeSubPage: subStep, nextStep: nextSlug ? stepArray[nextSlug + 1] : stepArray[1] };
      }
    }
    return { activePage: null, activeSubPage: null, nextStep: stepArray[0] };
  };

  const { activePage, activeSubPage, nextStep } = findMatchingStep(steps);

  return { activePage, activeSubPage, nextStep };
}


const WizardContent: React.FC<{
  activePage: SpcStep | null;
  activeSubPage: SpcStep['subSteps'][0] | null;
  handleNext: (index: number, onStepChange: any) => void;
  handleBack: () => void;
  handleNextStep: () => void;
  isLoading: boolean;
  formData: any;
}> = React.memo(({
  activePage,
  activeSubPage,
  handleNext,
  handleBack,
  handleNextStep,
  isLoading,
  // formData
}) => {
  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      {activePage?.navigatedFromReviewScreen && <div className="gap-4 p-6 border-[2px] border-orange-500 bg-[#fbf5ef] flex rounded-md mb-4" >
        <div><svg viewBox="0 0 29 26" fill="none" xmlns="http://www.w3.org/2000/svg" className='w-7 h-7'>
          <path d="M14.4989 0.5C15.3033 0.5 16.0453 0.918527 16.4532 1.60491L28.6884 22.1406C29.1019 22.8326 29.1019 23.6864 28.6998 24.3783C28.2976 25.0703 27.5442 25.5 26.7342 25.5H2.2637C1.45368 25.5 0.700303 25.0703 0.298126 24.3783C-0.104051 23.6864 -0.0983867 22.827 0.309455 22.1406L12.5447 1.60491C12.9525 0.918527 13.6946 0.5 14.4989 0.5ZM14.4989 7.64286C13.7456 7.64286 13.1395 8.23996 13.1395 8.98214V15.2321C13.1395 15.9743 13.7456 16.5714 14.4989 16.5714C15.2523 16.5714 15.8584 15.9743 15.8584 15.2321V8.98214C15.8584 8.23996 15.2523 7.64286 14.4989 7.64286ZM16.3116 20.1429C16.3116 19.6693 16.1206 19.2151 15.7807 18.8802C15.4407 18.5453 14.9797 18.3571 14.4989 18.3571C14.0182 18.3571 13.5572 18.5453 13.2172 18.8802C12.8773 19.2151 12.6863 19.6693 12.6863 20.1429C12.6863 20.6165 12.8773 21.0707 13.2172 21.4055C13.5572 21.7404 14.0182 21.9286 14.4989 21.9286C14.9797 21.9286 15.4407 21.7404 15.7807 21.4055C16.1206 21.0707 16.3116 20.6165 16.3116 20.1429Z" fill="#CF7119" />
        </svg>
        </div>
        <div className='-mt-1'>
          <div className='text-orange-500 text-22px font-semibold mb-1 tracking--1percent'>You Are Making a Change to Your Application</div>
          <p className="text-black text-15px tracking--1_5percent  ">
            You're updating part of your application. If you're changing your retirement date, marital status, or benefit option, we may ask you to re-enter info in other sections too. These details are connected and help us make sure your benefits are calculated correctly.
            <br /><br />If you don't wish to make any changes and want to go back, just click 'Cancel & Return to Review' to head back to your application summary.
          </p>
        </div>
      </div>
      }
      <Outlet />
      <div className="my-3 flex space-x-4">
        {(activeSubPage && activeSubPage.hideNextButton) || (!activeSubPage && activePage?.hideNextButton) ? null : (
          <Button
            rounded
            onClick={() => {
              if (activeSubPage && activeSubPage.onStepChange) {
                handleNext(1, activeSubPage.onStepChange);
              } else if (activePage && activePage.onStepChange) {
                handleNext(1, activePage.onStepChange);
              } else {
                handleNextStep();
              }
            }}
            label={
              activeSubPage?.nextButtonLabel
                ? activeSubPage?.nextButtonLabel
                : activePage?.nextButtonLabel
                  ? activePage.nextButtonLabel
                  : 'Save & Continue'
            }
          />
        )}

        {(activeSubPage && activeSubPage.hideCancelButton) || (!activeSubPage && activePage?.hideCancelButton) ? null : (
          <Button
            outlined
            severity="warning"
            label={
              activeSubPage?.cancelButtonLabel
                ? activeSubPage?.cancelButtonLabel
                : activePage?.cancelButtonLabel
                  ? activePage.cancelButtonLabel
                  : 'Cancel'
            }
            onClick={() => handleBack()}
          />
        )}
      </div>
    </>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.activePage?.slug === nextProps.activePage?.slug &&
    prevProps.activeSubPage?.slug === nextProps.activeSubPage?.slug &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.formData?.errors === nextProps.formData?.errors
  );
});


const WizardLayout = ({ retirementSteps }: { retirementSteps: SpcStep[] }) => {
  const { formData, updateFormData } = useForm();
  const stepArray = generateStepArray(retirementSteps);

  useEffect(() => {
    updateFormData('retirementSteps', retirementSteps);
    updateFormData('stepsFlatList', stepArray);
  }, [retirementSteps]);

  // }, [submissionId]);
  const location = useLocation();
  const { activePage, activeSubPage, nextStep } = useActiveStep(retirementSteps, stepArray, location);

  useEffect(() => {
    updateFormData('activeStep', activePage);
    updateFormData('activeSubStep', activeSubPage);
    updateFormData(
      'activeStepNumber',
      retirementSteps.findIndex((s: any) => s.slug === activePage?.slug),
    );
  }, [activePage, activeSubPage]);

  const navigate = useNavigate();

  const handleBack = () => {
    const prevStepIndex = stepArray.findIndex((s: any) => s.slug.includes(activeSubPage?.slug ? activeSubPage.slug : activePage?.slug)) - 1;
    const prevStep = stepArray[prevStepIndex];
    navigate(prevStep.slug);
  };

  const handleNext = async (index: number, onStepChange: any) => {
    if (typeof onStepChange === 'function') {
      const valid = await onStepChange();
      if (valid) {
        handleNextStep();
      } else {
        // alertService.showError("Validation failed")
        // updateFormData('errors', 'Validation failed');
      }
    }
  };

  const handleNextStep = () => {
    const nextStepIndex = stepArray.findIndex((s: any) => s.slug.includes(activeSubPage?.slug ? activeSubPage.slug : activePage?.slug)) + 1;
    const nextStep = stepArray[nextStepIndex];
    navigate(nextStep.slug);
  };

  const [isLoading, setIsLoading] = useState(false);

  // Scroll to page title on route change
  useEffect(() => {
    const scrollToPageTitle = () => {
      // Check if we're on mobile (screen width less than 768px)
      const isMobile = window.innerWidth < 768;
      
      if (isMobile) {
        // On mobile, scroll to content div to account for navigation space
        const contentDiv = document.getElementById('content');
        if (contentDiv) {
          const rect = contentDiv.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const targetScrollTop = scrollTop + rect.top;
          
          window.scrollTo({ 
            top: targetScrollTop, 
            behavior: 'smooth' 
          });
        }
      } else {
        // On desktop, just scroll to top
        window.scrollTo({ 
          top: 0, 
          behavior: 'smooth' 
        });
      }
    };
    // Small delay to ensure the new page content is rendered
    const timer = setTimeout(scrollToPageTitle, 50);
    return () => clearTimeout(timer);
 
  }, [location.pathname]);

  return (
    <div className="mx-auto px-6 lg:px-12 py-4">
      <div className="flex flex-wrap md:flex-nowrap">
        <div className="w-[320px] flex-shrink-0 md:pr-12">
          <p className="text-22px font-semibold text-neutral-800 mb-4">Service Purchase Calculator</p>
          <SidebarFormNavigation retirementSteps={retirementSteps} />
        </div>
        <div className='max-w-[1000px]' id="content">
          {formData.errors && formData.errors.length > 0 && (
            <div className="bg-red-500 text-white p-2 mb-4 text-sm rounded-xl text-center">
              {JSON.stringify(formData.errors)}
            </div>
          )}
          
          <WizardContent
            activePage={activePage}
            activeSubPage={activeSubPage}
            handleNext={handleNext}
            handleBack={handleBack}
            handleNextStep={handleNextStep}
            isLoading={isLoading}
            formData={formData}
          />
        </div>
      </div>
    </div>
  );
}


const SpcLayout: React.FC = observer(() => {
  const vm = Appl.Services.get<SpcViewModel>('SpcViewModel');
  vm.navigate = useNavigate();

  let { memberId } = useParams();
  const searchParams = new URLSearchParams(location.search);

  useAsyncEffect(async () => {
    let spcTypeId = Number(searchParams?.get('spcTypeId'));
    let spcTypeSubId = Number(searchParams?.get('spcTypeSubId'));
    vm.model.spcTypeId = spcTypeId;
    // vm.model.submissionId
    // await vm.loadStepAsync(SpcSteps.ServiceType, memberId!,spcTypeId,spcTypeSubId);
  }, []);1

  const url_location = useLocation();
  useEffect(
    function rehydrateCompletedStepsFromRoute()
    {
      if (!location.pathname)
      {
        return;
      }

      const pathSegments = location.pathname.split('/').filter(Boolean);

      const stepSlug = pathSegments[pathSegments.length - 1];

      if (!stepSlug)
      {
        return;
      }

      vm.setCompletedSteps(stepSlug);
    },
    [location.pathname]
  );
    
  const retirementSteps: SpcStep[] = [
    {
      step: 'Service Type',
      slug: SpcSteps.ServiceType,
      element: <SpcServiceType />,
      nextButtonLabel: 'Continue',
      hideNextButton: true,
      hideCancelButton: true,
      status: vm.stepStatus.serviceType as SpcStepStatus,
      subSteps: [],

    },
    {
      step: 'About Estimate',
      // slug: `${SpcSteps.AboutEstimate}?spcTypeId=${vm.model.spcTypeId}&spcTypeSubId=${vm.model.submissionId} `,
      slug: SpcSteps.AboutEstimate,
      element: <AboutEstimate />,
      nextButtonLabel:"Continue",
      hideNextButton: true,
      hideCancelButton: true,
      onStepChange: async () => await vm.onAboutEstimateStepChangeAsync(),
      status: vm.stepStatus.aboutEstimate as SpcStepStatus,
      subSteps: [],
    },
    {
      step: 'Enter Details',
      slug: SpcSteps.EnterDetails,
      element: <EnterDetails />,
      nextButtonLabel:"Continue",
      hideNextButton: true,
      hideCancelButton: true,
      onStepChange: async () => await vm.onEnterDetailsStepChangeAsync(),
      status: vm.stepStatus.enterDetails as SpcStepStatus,
      subSteps: [],
    },
    {
      step: 'Summary',
      slug: SpcSteps.Summary,
      element: <Summary />,
      nextButtonLabel:"Continue",
      hideNextButton: true,
      hideCancelButton: true,
      status: vm.stepStatus.summary as SpcStepStatus,
      onStepChange: async () => await vm.onSummaryStepChangeAsync(),
      subSteps: [],
    },
    {
      step: 'Estimate',
      slug: SpcSteps.Estimate,
      element: <Estimate />,
      nextButtonLabel:"Continue",
      hideNextButton: true,
      hideCancelButton: true,
      status: vm.stepStatus.estimate as SpcStepStatus,
      // onStepChange: async () => await vm.onEstimateStepChangeAsync(),
      subSteps: [],
    }
  ];

  // const savedEstimatesStep = [
  // {
  //       step: 'Service Purchase Calculator',
  //       slug: '',
  //       element: <SpcSavedEstimates />,
  //       subSteps: []
  //     }
  //   ];

  return (
    <>
      <View
        id="SpcLayout"
        showMenuBar={false}
        menuItems={SraModule.MenuItems()}
        style={{ backgroundColor: '#fff' }}
        className='py-4'
        container={false}>
        <SpcFormProvider>
          <Routes>
            {/* <Route index element={<WizardLayout retirementSteps={savedEstimatesStep} />}/> */}
            <Route index element={<div className="px-2 md:px-6 py-4"><SpcSavedEstimates /></div>}/>
            <Route path="/*" element={<WizardLayout retirementSteps={retirementSteps} />}>
              <Route element={<Navigate to={SpcSteps.ServiceType} />} />
              {retirementSteps.map((step, index) => {
                return (
                  <>
                    <Route
                      key={step.slug}
                      path={step.slug + '/*'}
                      element={
                        step.element ? (
                          step.element
                        ) : (
                          <div>
                            <h1>Invalid route</h1>
                          </div>
                        )
                      }
                    />

                    {step.subSteps.map((subStep, subIndex) => {
                      return (
                        <Route
                          key={step.slug + '/' + subStep.slug + '/*'}
                          path={step.slug + '/' + subStep.slug + '/*'}
                          element={
                            subStep.element ? (
                              subStep.element
                            ) : (
                              <div>
                                <h1>Invalid route</h1>
                              </div>
                            )
                          }
                        />
                      );
                    })}
                  </>
                );
              })}
            </Route>
          </Routes>
        </SpcFormProvider>
      </View>
    </>
  );
});
export default SpcLayout;
